import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Badge } from "../Badge";
import { REFERRAL_STATUSES } from "./Referrals";
import { ArrowLeft, UserPlus, Send } from "lucide-react";

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="mt-1 text-[15px] text-brand-navy font-medium whitespace-pre-wrap">{value || "—"}</p>
  </div>
);

const Section = ({ title, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
    <h2 className="font-display text-xl text-brand-navy">{title}</h2>
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>
  </section>
);

export default function ReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [status, setStatus] = useState("");
  const [staff, setStaff] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/referrals/${id}`).then((res) => {
    setR(res.data);
    setStatus(res.data.status);
    setStaff(res.data.assigned_staff || "");
  }).catch(() => setR(false));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (r === null) return <p className="text-slate-500 font-semibold">Loading referral…</p>;
  if (r === false) return <p className="text-red-700 font-semibold">Referral not found.</p>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/referrals/${id}`, { status, assigned_staff: staff });
      toast.success("Referral updated.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/referrals/${id}/notes`, { text: note });
      setNote("");
      toast.success("Internal note added.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const convert = async () => {
    if (!window.confirm("Convert this referral to a patient profile? The original referral will be kept.")) return;
    try {
      const res = await api.post(`/admin/referrals/${id}/convert`);
      toast.success("Patient profile created.");
      navigate(`/portal/patients/${res.data.patient_id}`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="referral-detail">
      <Link to="/portal/referrals" data-testid="back-to-referrals" className="inline-flex items-center gap-2 text-sm font-bold text-brand-teal hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> Back to Referrals
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-brand-navy">{r.patient_name}</h1>
          <p className="mt-1 text-sm text-slate-500">Referral #{r.id.slice(0, 8)} · Submitted {new Date(r.submitted_at).toLocaleString()}</p>
        </div>
        <Badge kind="referral" value={r.status} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h2 className="font-display text-xl text-brand-navy">Status & Assignment</h2>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="ref-status" className="block text-sm font-bold text-brand-navy">Referral Status</label>
            <select id="ref-status" data-testid="referral-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-2 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
              {REFERRAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ref-staff" className="block text-sm font-bold text-brand-navy">Assigned Office Staff</label>
            <input id="ref-staff" data-testid="referral-staff-input" type="text" value={staff} onChange={(e) => setStaff(e.target.value)} placeholder="Staff member name" className="mt-2 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60" />
          </div>
          <button onClick={save} disabled={saving} data-testid="referral-save-status" className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60">
            {saving ? "Saving…" : "Save Changes"}
          </button>
          {r.patient_id ? (
            <Link to={`/portal/patients/${r.patient_id}`} data-testid="view-patient-link" className="rounded-full bg-brand-navy px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-teal transition-colors duration-300">
              View Patient Profile
            </Link>
          ) : (
            <button onClick={convert} data-testid="convert-to-patient" className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-teal transition-colors duration-300 active:scale-95">
              <UserPlus className="h-4 w-4" /> Convert to Patient
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Patient Information">
          <Info label="Full Name" value={r.patient_name} />
          <Info label="Date of Birth" value={r.patient_dob} />
          <Info label="Phone Number" value={r.patient_phone} />
          <Info label="Email Address" value={r.patient_email} />
          <Info label="Home Address" value={r.patient_address} />
        </Section>

        <Section title="Emergency / Family Contact">
          <Info label="Name" value={r.emergency_name} />
          <Info label="Relationship to Patient" value={r.emergency_relationship} />
          <Info label="Phone Number" value={r.emergency_phone} />
          <Info label="Email" value={r.emergency_email} />
        </Section>

        <Section title="Referring Healthcare Professional / Clinic">
          <Info label="Physician / Healthcare Professional" value={r.referrer_name} />
          <Info label="Clinic / Organization" value={r.referrer_clinic} />
          <Info label="Phone Number" value={r.referrer_phone} />
          <Info label="Email" value={r.referrer_email} />
        </Section>

        <Section title="Reason for Referral">
          <div className="sm:col-span-2"><Info label="Reason" value={r.reason} /></div>
        </Section>

        <Section title="Requested Home Care Support">
          <div className="sm:col-span-2">
            <ul className="list-disc list-inside text-[15px] text-brand-navy font-medium space-y-1">
              {(r.requested_support || []).map((s) => <li key={s}>{s}</li>)}
              {r.other_support && <li>Other: {r.other_support}</li>}
              {(!r.requested_support || r.requested_support.length === 0) && !r.other_support && <li>None selected</li>}
            </ul>
          </div>
        </Section>

        <Section title="Relevant Care Considerations">
          <div className="sm:col-span-2"><Info label="Care Considerations" value={r.care_considerations} /></div>
        </Section>

        <Section title="Preferred Service Start Date">
          <Info label="Preferred Start Date" value={r.preferred_start_date} />
          <Info label="Preferred Days / Hours" value={r.preferred_days_hours} />
        </Section>

        <Section title="Consent & Signature">
          <Info label="Consent" value={r.consent ? "Confirmed — patient or authorized representative consented to share information with BMS Health Care Services Ltd." : "Not given"} />
          <Info label="Signed By" value={r.signature_name} />
          <Info label="Signature Date" value={r.signature_date} />
        </Section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Internal Notes</h2>
          <p className="mt-1 text-xs text-slate-500">Private — visible only to authorized office staff. Never shown publicly or to caregivers.</p>
          <ul className="mt-5 space-y-3">
            {(r.internal_notes || []).length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
            {(r.internal_notes || []).map((n) => (
              <li key={n.id} className="rounded-xl bg-brand-mist/60 px-4 py-3">
                <p className="text-[15px] text-brand-navy">{n.text}</p>
                <p className="mt-1 text-xs text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
          <form onSubmit={addNote} className="mt-5 flex gap-3">
            <input
              data-testid="referral-note-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Called family, left voicemail…"
              className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60"
            />
            <button type="submit" data-testid="referral-note-add" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
              <Send className="h-4 w-4" /> Add Note
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
